import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdSlot from '@gumtree/third-party-ads/src/components/ad-slot';
import refreshGoogleAds from '@gumtree/third-party-ads/src/refresh-google-ads';
import { DisplayAdSlotDivNames } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import type { ImageSlide } from '@gumtree/ui-library/src/carousel/slider';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@gumtree/ui-library/src/base/theme';
import GalleryDialog from './GalleryDialog';
import { openDialog, closeDialog } from '../../reducers/image-dialog';
import { VipState } from '../../reducers';
import * as S from './index.style';

interface ImageCarouselProps {
    eleId?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({eleId}) => {
    // const images = [
    //     'https://imagedelivery.net/0dtzPpMeji4d4WCWWfEHVw/6b026a44-7057-40dd-557d-ad6e01ac9000/86',
    //     'https://imagedelivery.net/0dtzPpMeji4d4WCWWfEHVw/6b026a44-7057-40dd-557d-ad6e01ac9000/86',
    //     // 'https://imagedelivery.net/0dtzPpMeji4d4WCWWfEHVw/6b026a44-7057-40dd-557d-ad6e01ac9000/86',
    //     // 'https://imagedelivery.net/0dtzPpMeji4d4WCWWfEHVw/6b026a44-7057-40dd-557d-ad6e01ac9000/86',
    //     // 'https://imagedelivery.net/0dtzPpMeji4d4WCWWfEHVw/6b026a44-7057-40dd-557d-ad6e01ac9000/86',
    // ]
    const {
        images,
        // baseConfig: { device },
    } = useSelector((state: VipState) => state);
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({ maxWidth: breakpoints.medium });
    const mediaArray = images.map((image) => ({ type: 'image', link: image })) as ImageSlide[]

    const [maxImagesPc, setMaxImagesPc] = useState(4); // 初始值设为4，或根据情况调整
    const scrollWrapperRef = useRef<HTMLDivElement>(null);
    const imageItemRef = useRef<HTMLDivElement>(null); // 用于测量单个图片项的宽度
    const adSlotRef = useRef<HTMLDivElement>(null); // 用于检测广告可见性
    const hasAdFired = useRef(false); // 跟踪广告是否已经触发过加载

    // 弹窗的数据
    const isDialogOpen = useSelector(({ imageDialog }: VipState) => !!imageDialog.isDialogOpen);
    const slideNumber = useSelector(({ imageDialog }: VipState) => imageDialog.slideNumber);
    const adTitle = useSelector(({ adDetails }: VipState) => adDetails.title);

    // 用于跟踪广告是否应该展示的状态
    const prevShouldShowAdRef = useRef<boolean>();

    useEffect(() => {
        const calculateMaxImages = () => {
            if (isMobile || !scrollWrapperRef.current || !imageItemRef.current) {
                return;
            }

            const scrollWrapperWidth = scrollWrapperRef.current.offsetWidth;
            const imageItemStyle = window.getComputedStyle(imageItemRef.current);
            const imageItemWidth = imageItemRef.current.offsetWidth + 
                                   parseFloat(imageItemStyle.marginLeft) + 
                                   parseFloat(imageItemStyle.marginRight);

            if (imageItemWidth === 0) return;
            
            const calculatedMax = Math.floor(scrollWrapperWidth / imageItemWidth);
            // 确保至少显示一张图片，除非容器宽度不够
            setMaxImagesPc(calculatedMax > 0 ? calculatedMax : 1); 
        };

        calculateMaxImages(); // 初次计算

        window.addEventListener('resize', calculateMaxImages);
        return () => {
            window.removeEventListener('resize', calculateMaxImages);
        };
    }, [isMobile]); // 依赖项调整，确保maxImagesPc变化时也可能重新计算广告位逻辑

    // 展示图片处理
    let imagesToDisplay = mediaArray;
    let remainingImagesCount = 0;
    // 使用动态计算的 maxImagesPc
    const exceedImgPc = !isMobile && mediaArray.length > maxImagesPc;
    const fullImgPc = !isMobile && mediaArray.length >= maxImagesPc;

    // 判断广告是否应该展示
    const shouldShowAd = !isDialogOpen && !fullImgPc;

    if (exceedImgPc) {
        imagesToDisplay = mediaArray.slice(0, maxImagesPc);
        remainingImagesCount = mediaArray.length - maxImagesPc;
    }

    const handleImageClick = (index: number) => {
        trackGA4Event({
            event: 'click_image',
        });
        dispatch(openDialog(index + 1))
    };

    useEffect(() => {
        // 仅当广告从不展示变为展示时，刷新广告
        if (shouldShowAd && prevShouldShowAdRef.current === false) {
            // @ts-ignore
            refreshGoogleAds(window as WindowWithGoogleTag, [
                DisplayAdSlotDivNames.galleryMPU,
            ]);
            console.log('shouldShowAd refreshGoogleAds---2');
        }
        
        // 当广告状态变为false时，重置视口检测状态
        if (!shouldShowAd && prevShouldShowAdRef.current === true) {
            hasAdFired.current = false;
        }
        
        // 更新上一次的广告展示状态
        prevShouldShowAdRef.current = shouldShowAd;
    }, [shouldShowAd]);

    useEffect(() => {
        // 如果初始就应该展示广告，则加载它
        if (shouldShowAd) {
            // @ts-ignore
            refreshGoogleAds(window as WindowWithGoogleTag, [
                DisplayAdSlotDivNames.galleryMPU,
            ]);
            console.log('shouldShowAd refreshGoogleAds---1');
        }
    }, [])

    // 视口检测逻辑：当广告滑入视口时再次加载广告
    useEffect(() => {
        if (
            hasAdFired.current ||
            !shouldShowAd ||
            !adSlotRef.current ||
            !(adSlotRef.current as HTMLElement).parentNode
        ) {
            return;
        }

        // 检查 IntersectionObserver 是否支持
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (entry.isIntersecting && !hasAdFired.current) {
                        // 确保回调只触发一次
                        hasAdFired.current = true;
                        // 广告进入视口时加载
                        // @ts-ignore
                        refreshGoogleAds(window as WindowWithGoogleTag, [
                            DisplayAdSlotDivNames.galleryMPU,
                        ]);
                        console.log('adSlotRef.current refreshGoogleAds');
                        observer.disconnect(); // 触发后断开观察器
                    }
                },
                {
                    root: null, // 使用视口作为根
                    rootMargin: '50px', // 提前50px开始检测
                    threshold: 0.1, // 当10%的广告可见时触发
                }
            );

            if (adSlotRef.current) {
                observer.observe(adSlotRef.current);
            }

            return () => {
                observer.disconnect();
            };
        }
        return;
    }, [shouldShowAd]); // 依赖 shouldShowAd 来正确处理状态变化

    // 移动端滚动检测作为备用方案
    useEffect(() => {
        if (
            hasAdFired.current ||
            !shouldShowAd ||
            !isMobile ||
            !scrollWrapperRef.current ||
            !adSlotRef.current
        ) {
            return;
        }

        const handleScroll = () => {
            if (hasAdFired.current) return;

            const scrollWrapper = scrollWrapperRef.current;
            const adSlot = adSlotRef.current;
            
            if (!scrollWrapper || !adSlot) return;

            const scrollLeft = scrollWrapper.scrollLeft;
            const scrollWidth = scrollWrapper.scrollWidth;
            const clientWidth = scrollWrapper.clientWidth;
            
            // 检查是否滚动到最右侧（允许10px的误差）
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;
            
            if (isAtEnd) {
                hasAdFired.current = true;
                // @ts-ignore
                refreshGoogleAds(window as WindowWithGoogleTag, [
                    DisplayAdSlotDivNames.galleryMPU,
                ]);
                console.log('mobile scroll end refreshGoogleAds');
            }
        };

        const scrollWrapper = scrollWrapperRef.current;
        scrollWrapper.addEventListener('scroll', handleScroll, { passive: true });
        
        // 初始检查一次
        handleScroll();

        return () => {
            scrollWrapper.removeEventListener('scroll', handleScroll);
        };
    }, [shouldShowAd, isMobile]);

    const closeModal = () => {
        dispatch(closeDialog())
    }

    if (!mediaArray || mediaArray.length === 0) {
        return null;
    }

    return (
        <S.CarouselContainer id={eleId}>
            <S.CarouselTitle>Portfolio</S.CarouselTitle>
            {/* 将 ref 赋给 S.ScrollWrapper */}
            <S.ScrollWrapper ref={scrollWrapperRef}>
                {imagesToDisplay.map((image, index) => (
                    <S.ImageItem 
                        ref={index === 0 ? imageItemRef : null} 
                        key={image.link} 
                        onClick={() => handleImageClick(index)}
                    >
                        <img src={image.link} alt={image.type ?? `Image ${index + 1}`} />
                        {!isMobile && index === imagesToDisplay.length - 1 && remainingImagesCount > 0 && (
                            <S.ImageItemOverlay>
                                +{remainingImagesCount}
                            </S.ImageItemOverlay>
                        )}
                    </S.ImageItem>
                ))}
                {
                    shouldShowAd && (
                        <S.ImageItem ref={adSlotRef}>
                            <AdSlot 
                                id={DisplayAdSlotDivNames.galleryMPU} 
                                // css={css`
                                //     width: 175px;
                                //     height: 175px;
                                //     /* display: flex;
                                //     justifyContent: center;
                                //     alignItems: center; */
                                // `}
                                // style={{
                                //     width: '175px',
                                //     height: '175px',
                                //     display: 'flex',
                                //     justifyContent: 'center',
                                //     alignItems: 'center',
                                // }}
                            />
                        </S.ImageItem>
                    )
                }
            </S.ScrollWrapper>

            <GalleryDialog
                isOpen={!!isDialogOpen}
                onClose={closeModal}
                mediaLinks={mediaArray} // 弹窗始终使用完整的 mediaArray
                slideNumber={slideNumber}
                imageAltTitle={adTitle}
                hasControls
                hasIcon
                hasCounter
                endTracking="GalleryEnd"
                trackingContext="VIPImages"
            />
        </S.CarouselContainer>
    );
};

export default ImageCarousel;
